import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Drawer,
  FormControl,
  Grid,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material';

import { isUrlValid } from '../../../../helper';
import {
  IAdminAddSubTopicVideoLink,
  IAdminEditSubTopicVideoLink,
  ISubTopicVideoLink,
  adminAddSubTopicVideoLink,
  adminEditSubTopicVideoLink,
} from '../../../../store/SubTopicSlice';

interface AddNewVideoLinkModalProps {
  open: boolean;
  onClose: () => void;
  editMode?: boolean;
  subTopicVideoLink?: ISubTopicVideoLink;
}

interface IState {
  title: string;
  link: string;
}

const resourcesExplorerContainerStyles: React.CSSProperties = {
  minHeight: 'calc(100vh - 62px - 24px - 40px)',
  maxHeight: 'calc(100vh - 62px - 24px - 40px)',
  overflowY: 'auto',
  padding: '0 15px',
};

const labelProps: TypographyProps = {
  fontWeight: 600,
  variant: 'subtitle2',
  style: { marginBottom: '3px' },
};

const initialState: IState = {
  title: '',
  link: '',
};

const AddNewVideoLinkModal: React.FC<AddNewVideoLinkModalProps> = ({
  open,
  onClose,
  editMode = false,
  subTopicVideoLink = undefined,
}) => {
  const dispatch = useDispatch();
  const urlParams = useParams();

  // State
  const [state, setState] = useState(initialState);

  const isLinkValid = isUrlValid(state.link);

  useEffect(() => {
    if (open) {
      if (editMode && subTopicVideoLink) {
        setState({
          ...initialState,
          title: subTopicVideoLink.title,
          link: subTopicVideoLink.link,
        });
      }
    } else {
      setState(initialState);
    }
  }, [open]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    if (urlParams.subTopicId) {
      if (editMode) {
        if (subTopicVideoLink) {
          const apiData: IAdminEditSubTopicVideoLink = {
            subTopicId: urlParams.subTopicId,
            videoLinkId: subTopicVideoLink._id,
            title: state.title.trim(),
            link: state.link.trim(),
          };

          dispatch(adminEditSubTopicVideoLink(apiData));
        }
      } else {
        const apiData: IAdminAddSubTopicVideoLink = {
          subTopicId: urlParams.subTopicId,
          title: state.title.trim(),
          link: state.link.trim(),
        };

        dispatch(adminAddSubTopicVideoLink(apiData));
      }
    }

    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      PaperProps={{ sx: { minWidth: '350px', maxWidth: '350px' } }}
    >
      <Box role="presentation">
        <div style={{ padding: '15px' }}>
          <Typography variant="h6" fontWeight={600}>
            Upload a new video
          </Typography>
        </div>
        <div style={resourcesExplorerContainerStyles}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography {...labelProps}>Video Title</Typography>
              <FormControl fullWidth>
                <TextField
                  multiline
                  rows={2}
                  name="title"
                  placeholder="Enter here..."
                  value={state.title}
                  onChange={handleInputChange}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography {...labelProps}>
                Paste Your Video Link Here
              </Typography>
              <FormControl fullWidth>
                <TextField
                  name="link"
                  placeholder="Paste here..."
                  value={state.link}
                  onChange={handleInputChange}
                  InputProps={
                    isLinkValid
                      ? {
                          sx: {
                            color: 'info.main',
                            textDecoration: 'underline',
                          },
                        }
                      : undefined
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
        </div>
        <Box
          style={{ padding: '10px 15px', backgroundColor: 'background.paper' }}
        >
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={
              state.link.trim().length === 0 ||
              state.title.trim().length === 0 ||
              !isLinkValid
            }
          >
            {editMode ? 'Save' : 'Add'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddNewVideoLinkModal;
